/**
 * Shared data access for the "Account & People" area.
 *
 * The Composition API equivalent of `account-admin-mixin.js` — new pages import
 * these functions directly; the mixin now delegates here so the account-id
 * resolution and the account load exist in exactly one place (a lot of older
 * Options API pages still mix it in).
 */

import api2 from "/api2.js";
import globalStore from "/globalStore.js";


/**
 * The account ID an account-admin page operates on: the `accountId` route param
 * when the URL carries one, then the account a system admin has selected, and
 * finally the signed-in user's own account.
 *
 * @param {object} route The current vue-router route (`useRoute()`).
 */
export function resolveAccountAdminAccountId(route)
{
	const routeAccountId = route && route.params && route.params.accountId ? route.params.accountId : "";
	if (routeAccountId != "") return routeAccountId;

	if (globalStore.state.systemAdminSelectedAccountId != "") return globalStore.state.systemAdminSelectedAccountId;

	return globalStore.state.currentUser.activeAccountId;
}


/**
 * Load an account, mapped to the shape the account-admin pages bind to.
 *
 * @returns {object|null} The account, or null when the call failed.
 */
export async function getAccount(accountId)
{
	const request = { accountId: accountId };

	const responseData = await api2.callApi1("GET", "/Accounts/GetAccount", request);
	if (responseData == null) return null;

	return { ...mapAccount(responseData.Account), isSystemAccount: responseData.IsSystemAccount === true };
}


/**
 * Create or update an account.
 *
 * @param {object} account The page's account object (see `mapAccount()` for the shape).
 * @returns {object|null} The saved account, or null when the call failed.
 */
export async function saveAccount(account)
{
	const request = {
		accountId: account.id,
		name: account.name,
		businessName: account.name,
		status: account.status,
		defaultTimezoneCurrentOffset: account.defaultTimezoneCurrentOffset,
		defaultTimezoneID: account.defaultTimezoneID,
	};

	const responseData = await api2.callApi2WithOverlay("POST", "/Accounts/CreateOrUpdateAccount", request);
	if (responseData == null) return null;

	return mapAccount(responseData.Account);
}


/**
 * The API's Account record as the pages hold it.
 */
export function mapAccount(account)
{
	return {
		id: account.ID,
		name: account.Name,
		status: account.Status,
		defaultTimezoneID: account.DefaultTimezoneID,
		defaultTimezoneCurrentOffset: account.DefaultTimezoneUTCOffset,
	};
}


/**
 * Every person in the account, sorted by first then last name.
 */
export async function getAllAccountUsers(accountId)
{
	const request = { accountId: accountId };

	const responseData = await api2.callApi2WithOverlay("GET", "/Users/GetAllAccountUsers/v2", request);
	if (responseData == null) return [];

	return [...responseData.AccountUsers].sort(byFirstThenLastName);
}


/**
 * Every organisation role defined in the account.
 */
export async function getAllOrganisationRoles(accountId)
{
	const request = { accountId: accountId };

	const responseData = await api2.callApi2WithOverlay("GET", "/OrganisationRoles/GetAllRolesForAccount", request);
	if (responseData == null) return [];

	return responseData.OrganisationRoles;
}


/**
 * Every client in the account. No overlay — accounts with a large client list
 * shouldn't be blocked while this loads in the background.
 */
export async function getAllClientsInAccount(accountId)
{
	const request = {
		accountId: accountId,
		pageNumber: 1,
		pageSize: -1,
	};

	const responseData = await api2.callApi1("GET", "/Clients/GetAllClientsInAccount", request);
	if (responseData == null) return [];

	return responseData.PagedResult.Data;
}


function byFirstThenLastName(item1, item2)
{
	if (item1.FirstName.toLowerCase() > item2.FirstName.toLowerCase()) return 1;
	if (item1.FirstName.toLowerCase() < item2.FirstName.toLowerCase()) return -1;

	if (item1.LastName.toLowerCase() > item2.LastName.toLowerCase()) return 1;
	if (item1.LastName.toLowerCase() < item2.LastName.toLowerCase()) return -1;

	return 0;
}
